/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import { SimpleTextInput } from "components/FormTextInput";
import StatusBadge from "components/StatusBadge";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getTransactionInfo,
  getReconciliationHistory,
} from "services/reportsService";
import sessionHelperInstance from "services/sessionHelper";
import { userRoles } from "utils/constants";
import GoalScorersModal from "./components/GoalScorersModal";
import ReconcileDialog from "./components/ReconcileDialog";
import ReconciliationHistoryModal from "./components/ReconciliationHistoryModal";
import MatchEventsTimeline from "./components/MatchEventsTimeline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export const getNestedValue = (obj, path, defaultValue = null) => {
  if (!obj || !path) return defaultValue;

  const val = path.split(".").reduce((acc, key) => {
    if (acc && acc[key] !== undefined) {
      return acc[key];
    }
    return defaultValue;
  }, obj);

  // Handle boolean values explicitly
  if (typeof val === "boolean") {
    return val ? "true" : "false";
  }

  return val ?? defaultValue; // Ensure nullish values are replaced with defaultValue
};

const styles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  actionsContainer: {
    display: "flex",
    height: 40,
    gap: 10,
  },
  detailContainer: {
    background: "#fff",
    borderRadius: 12,
    width: "100%",
  },
  detailText: {
    fontWeight: 600,
    fontSize: 18,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
  },
  lightBackground: {
    background: "#e0e0e0",
  },
  leftCell: {
    width: "50%",
    textAlign: "left",
    fontWeight: 500,
    padding: "6px 0 6px 6px",
  },
  rightCell: {
    width: "50%",
    textAlign: "left",
    padding: "6px 0 6px 6px",
  },
}));

const groupedDetails = {
  // Transaction Details
  Transaction_Details: [
    { id: "_id", label: "DB Id (RGS game_rounds)" },
    { id: "betTxnId", label: "Internal Bet Id" },
    { id: "platformTransactionId", label: "External Bet ticket ID" },
    { id: "platformBetTxnId", label: "Platform Bet Txn ID" },
    { id: "transactionType", label: "Transaction Type" },
    { id: "playerCurrencyCode", label: "User Currency" },
    { id: "betAmount", label: "Bet Amount" },
    { id: "winAmount", label: "Win Amount" },
    { id: "balance", label: "Balance" },
    { id: "status", label: "Status" },
    { id: "requestItemId", label: "Bet Request Item ID" },
    { id: "winRequestItemId", label: "Win Request Item ID" },
    { id: "refundTxnId", label: "Refund Txn ID" },
    { id: "numericRoundId", label: "GG Round ID (Numeric)" },
    { id: "platformErrorCode", label: "Platform Error Code" },
    { id: "resolved", label: "Bet Status" },
    { id: "isFreeBet", label: "Free Bet" },
    { id: "buyBonus", label: "Buy Bonus" },
  ],

  // Dates
  Dates: [
    { id: "betPlacedAt", label: "Bet placed Date" },
    { id: "betSettledAt", label: "Bet settled Date" },
    { id: "betRefundedAt", label: "Bet refunded Date" },
    { id: "gameBeBetInfo.startDate", label: "Match Start Date" },
    { id: "gameBeBetInfo.endDate", label: "Match End Date" },
  ],

  // Match Info
  Match_Info: [
    { id: "roundId", label: "Match Id" },
    { id: "gameBeBetInfo.matchStatus", label: "Match Status" },
    { id: "gameBeBetInfo.myTeamScore", label: "My Team Score" },
    { id: "gameBeBetInfo.oppTeamScore", label: "Opponent Team Score" },
  ],

  // Division Info
  Division_Info: [
    { id: "gameBeBetInfo.leagueInfo.currentLeague", label: "Division" },
    { id: "gameBeBetInfo.leagueInfo.currentMilestone", label: "Milestone" },
    { id: "gameBeBetInfo.leagueInfo.currentStep", label: "Step" },
    { id: "gameBeBetInfo.leagueInfo.initialOvr", label: "Initial OVR" },
  ],
  // RNG Info
  RNG_Info: [
    { id: "gameBeBetInfo.scorePickRNG", label: "Score Pick RNG" },
    {
      id: "gameBeBetInfo.extraData.selectKValue.randomNumber",
      label: "K Value Pick RNG",
    },
    {
      id: "gameBeBetInfo.extraData.selectKValue.kValue",
      label: "K Value Picked",
    },
  ],

  // My Team Info
  My_Team_Info: [
    { id: "gameBeBetInfo.myTeam.autoName", label: "My Team Unique ID" },
    { id: "gameBeBetInfo.myTeam.attackRating", label: "My Team ATT" },
    { id: "gameBeBetInfo.myTeam.defenseRating", label: "My Team DEF" },
    { id: "gameBeBetInfo.myTeam.creativityRating", label: "My Team CRE" },
    { id: "gameBeBetInfo.myTeam.overallRating", label: "My Team OVR" },
    { id: "gameBeBetInfo.myTeam.chemistry", label: "My Team Chemistry" },
  ],

  // Opponent Team Info
  Opponent_Team_Info: [
    { id: "gameBeBetInfo.opponentTeam._id", label: "Opponent Team DB ID" },
    { id: "gameBeBetInfo.opponentTeam.name", label: "Opponent Team Unique ID" },
    {
      id: "gameBeBetInfo.opponentTeam.attackRating",
      label: "Opponent Team ATT",
    },
    {
      id: "gameBeBetInfo.opponentTeam.defenseRating",
      label: "Opponent Team DEF",
    },
    {
      id: "gameBeBetInfo.opponentTeam.creativityRating",
      label: "Opponent Team CRE",
    },
    {
      id: "gameBeBetInfo.opponentTeam.overallRating",
      label: "Opponent Team OVR",
    },
    {
      id: "gameBeBetInfo.opponentTeam.chemistry",
      label: "Opponent Team Chemistry",
    },
    { id: "gameBeBetInfo.opponentTeam.isBot", label: "Is Bot?" },
    { id: "gameBeBetInfo.opponentTeam.user", label: "GameBe User ID" },
  ],

  // Market Info
  Market_Info: [
    { id: "extraData.oddDetails.name", label: "Market Name" },
    { id: "marketType", label: "Market Type" },
    { id: "extraData.oddDetails.goals", label: "Goals" },
    { id: "extraData.oddDetails.win", label: "Win" },
    { id: "extraData.oddDetails.teamId", label: "Team ID" },
    { id: "extraData.oddDetails.playerId", label: "Player ID" },
    { id: "extraData.oddDetails.team1", label: "Team 1 Score" },
    { id: "extraData.oddDetails.team2", label: "Team 2 Score" },
    { id: "extraData.oddDetails.value", label: "Value" },
    { id: "gameBeBetInfo.searchedBetInfo.oddsDetails.type", label: "Type" },
    {
      id: "gameBeBetInfo.searchedBetInfo.oddsDetails.handicap",
      label: "Handicap",
    },
  ],

  // Odd Info
  Odd_Info: [
    { id: "price", label: "Probability" },
    { id: "oddAfterMargin", label: "Odd" },
    { id: "extraData.oddsId", label: "Odds DB ID" },
    { id: "extraData.oddDetails.operatorMargin", label: "Operator Margin%" },
    { id: "extraData.payoutMultiplier", label: "Payout Multiplier" },
  ],

  // XP Info
  XP_Info: [
    { id: "extraData.xp", label: "XP" },
    { id: "extraData.xpBoostPercentage", label: "XP Boost%" },
  ],

  // Operator Info
  Operator_Info: [
    { id: "operator.operatorName", label: "Operator Name" },
    { id: "operator._id", label: "Internal Operator Id" },
    { id: "operator.externalOpId", label: "External Operator Id" },
  ],

  // User Info
  User_Info: [
    { id: "externalPlayerId", label: "External User Id" },
    { id: "user", label: "RGS User Id" },
    { id: "extraData.userId", label: "GameBE User Id" },
    { id: "playerSessionId", label: "User Session Id" },
    { id: "gameAlias", label: "Game ID" },
    { id: "playerName", label: "Player Name" },
  ],
};

// ─── Fields that are also available as filters in Transaction Report ─────────
const FILTERABLE_FIELD_IDS = new Set([
  "betTxnId",
  "platformTransactionId",
  "platformBetTxnId",
  "playerCurrencyCode",
  "requestItemId",
  "winRequestItemId",
  "refundTxnId",
  "numericRoundId",
  "resolved",
  "isFreeBet",
  "buyBonus",
  "roundId",
  "marketType",
  "externalPlayerId",
  "playerSessionId",
  "gameAlias",
  "playerName",
]);

const FIELD_DESCRIPTIONS: Record<string, string> = {
  betTxnId:
    "Internal RGS-generated bet transaction ID. Use 'Internal Bet Txn ID' filter to look up this round.",
  platformTransactionId:
    "External bet ticket ID from the operator's platform. Use 'External Bet Ticket ID' filter.",
  platformBetTxnId:
    "Platform's own bet transaction reference for reconciliation. Use 'Platform Bet Txn ID' filter.",
  playerCurrencyCode:
    "Currency in which the player placed this bet (e.g. USD, EUR). Use 'Currency Code' filter.",
  requestItemId:
    "Request item ID sent to the platform when placing the bet. Use 'Request Item ID' filter.",
  winRequestItemId:
    "Request item ID associated with the win payout. Use 'Win Request Item ID' filter.",
  refundTxnId:
    "Transaction ID of any refund linked to this round. Use 'Refund Txn ID' filter.",
  numericRoundId:
    "GamesGlobal numeric round ID. Filter also matches rotated round IDs. Use 'GG Round ID (Numeric)' filter.",
  resolved:
    "Final resolution of the bet: Won, Lost, or Void. Use 'Bet Status' filter.",
  isFreeBet:
    "True if this transaction was placed as a free bet (no real money staked). Use 'Free Bet' filter.",
  buyBonus: "True if this is a Buy Bonus transaction. Use 'Buy Bonus' filter.",
  roundId:
    "Unique match/round identifier from the Game Backend. Use 'Round ID' filter.",
  marketType:
    "Betting market type selected for this round (e.g. Final Result, BTTS). Use 'Market Type' filter.",
  externalPlayerId:
    "Player ID as registered with the operator's platform. Use 'External Player ID' filter.",
  playerSessionId:
    "Active session ID of the player when the bet was placed. Use 'Player Session ID' filter.",
  gameAlias:
    "Game alias/identifier string for this game variant. Use 'Game Alias' filter.",
  playerName:
    "Display name of the player who placed this bet. Use 'Player Name' filter.",
};

const SECTION_COLORS: Record<string, string> = {
  Transaction_Details: "#1565c0",
  Dates: "#6a1b9a",
  Match_Info: "#2e7d32",
  Division_Info: "#e65100",
  RNG_Info: "#0277bd",
  My_Team_Info: "#00695c",
  Opponent_Team_Info: "#b71c1c",
  Market_Info: "#4527a0",
  Odd_Info: "#4e342e",
  XP_Info: "#f57f17",
  Operator_Info: "#37474f",
  User_Info: "#880e4f",
};
// ─────────────────────────────────────────────────────────────────────────────

const RoundDetails = () => {
  const classes = styles();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { search } = useLocation();
  const [loading, setLoading] = React.useState(false);
  const [goalScorersModalOpen, setGoalScorersModalOpen] = React.useState(false);
  const [jsonSections, setJsonSections] = useState<Record<string, boolean>>({});
  const [roundDetails, setRoundDetails] = React.useState({
    betTxnId: new URLSearchParams(search).get("betTxnId") || "",
    info: null,
  });
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);

  const isSuperAdminDelete =
    sessionHelperInstance.role === userRoles.SUPER_ADMIN_DELETE;
  const isSuperAdminOrDelete = [
    userRoles.SUPER_ADMIN,
    userRoles.SUPER_ADMIN_DELETE,
  ].includes(sessionHelperInstance.role);
  const allowManualReconciliation =
    roundDetails.info?.operator?.allowManualReconciliation === true;
  const canReconcile = isSuperAdminDelete && allowManualReconciliation;

  const onChange = (e: any) =>
    setRoundDetails((prev) => ({ ...prev, betTxnId: e.target.value }));

  const goBack = () => navigate(-1);

  const toggleJsonSection = (key: string) =>
    setJsonSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const copyJson = (data: any) => {
    try {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      showSnackbar("Copied to clipboard", "success");
    } catch {
      showSnackbar("Copy failed", "error");
    }
  };

  const copyValue = (val: any) => {
    try {
      const str = Array.isArray(val)
        ? val.join(", ")
        : typeof val === "object" && val !== null
          ? JSON.stringify(val)
          : String(val);
      navigator.clipboard.writeText(str);
      showSnackbar("Copied to clipboard", "success");
    } catch {
      showSnackbar("Copy failed", "error");
    }
  };

  const fetchData = async (betId) => {
    setLoading(true);
    try {
      const resp = await getTransactionInfo(betId);

      setRoundDetails((prev) => ({
        ...prev,
        info: resp?.data,
      }));
      if (resp?.data?.betTxnId && isSuperAdminOrDelete) {
        getReconciliationHistory(resp.data.betTxnId)
          .then((histResp) => setHasHistory((histResp?.data?.length ?? 0) > 0))
          .catch(() => setHasHistory(false));
      }
      setLoading(false);
    } catch (error) {
      setRoundDetails((prev) => ({ ...prev, info: null }));
      setLoading(false);
      showSnackbar(error?.message, "error");
    }
  };

  useEffect(() => {
    if (roundDetails.betTxnId) fetchData(roundDetails.betTxnId);
  }, []);

  // Returns true only when there is a meaningful value to display for a column.
  const hasValue = (column: any, info: any): boolean => {
    if (column.id === "platformErrorCode") {
      return (
        info?.errorFromPlatform?.platformErrorCode != null ||
        info?.errorFromPlatform?.detailedMsg?.errorCode != null
      );
    }
    return getNestedValue(info, column.id) != null;
  };

  const renderCellValue = (column: any, info: any) => {
    if (column.id === "resolved") {
      return (
        <StatusBadge value={getNestedValue(info, column?.id)} type="resolved" />
      );
    }
    if (column.id === "status") {
      return (
        <StatusBadge
          value={getNestedValue(info, column?.id)}
          type="roundStatus"
        />
      );
    }
    if (column.id === "extraData.oddDetails.playerId") {
      return (
        <Box>
          <Typography fontWeight={"bold"} variant="body2">
            {getNestedValue(info, column?.id) ?? "--"}
          </Typography>
          {getNestedValue(info, column?.id) &&
            info?.gameBeBetInfo?.allPlayers && (
              <Typography
                variant="body2"
                color="primary"
                display="block"
                sx={{ mt: 0.5, fontWeight: "500" }}
              >
                {info.gameBeBetInfo.allPlayers.find(
                  (p: any) => p.playerId === getNestedValue(info, column?.id),
                )?.name || "Player not found"}
              </Typography>
            )}
        </Box>
      );
    }
    if (column.id === "platformErrorCode") {
      return (
        <Typography fontWeight={"bold"} variant="body2">
          {info?.errorFromPlatform?.platformErrorCode ??
            info?.errorFromPlatform?.detailedMsg?.errorCode ??
            "--"}
        </Typography>
      );
    }
    if (column.id === "isFreeBet" || column.id === "buyBonus") {
      const val = getNestedValue(info, column?.id);
      return (
        <Chip
          size="small"
          label={val === true ? "Yes" : val === false ? "No" : "--"}
          color={val === true ? "success" : "default"}
          variant="outlined"
          sx={{ fontWeight: 700, fontSize: 11 }}
        />
      );
    }
    return (
      <Typography fontWeight={"bold"} variant="body2">
        {getNestedValue(info, column?.id) ?? "--"}
      </Typography>
    );
  };

  return (
    <ContentShell
      title="Round Details"
      loading={loading}
      showBackButton={true}
      onBackButtonClick={goBack}
      actions={
        <div className={classes.actionsContainer}>
          <Box sx={{ width: 280 }}>
            <SimpleTextInput
              placeholder="Enter BetTxn ID or Bet ticket ID"
              name={"betTxnId"}
              value={roundDetails.betTxnId}
              onChange={onChange}
              customStyle={{ height: 35 }}
            />
          </Box>
          <Button
            sx={{ height: 35 }}
            variant="contained"
            onClick={() => fetchData(roundDetails.betTxnId)}
          >
            Get Info
          </Button>
          {canReconcile && roundDetails.info && (
            <Button
              sx={{ height: 35 }}
              variant="outlined"
              color="warning"
              onClick={() => setReconcileOpen(true)}
            >
              Reconcile
            </Button>
          )}
          {isSuperAdminOrDelete && hasHistory && roundDetails.info && (
            <Button
              sx={{ height: 35 }}
              variant="outlined"
              onClick={() => setHistoryOpen(true)}
            >
              Reconciliation History
            </Button>
          )}
        </div>
      }
    >
      <div className={classes.root}>
        {roundDetails.info && (
          <Box className={classes.detailContainer}>
            {Object.entries(groupedDetails).map(([sectionTitle, columns]) => {
              const accentColor = SECTION_COLORS[sectionTitle] ?? "#1976d2";
              const visibleColumns = columns.filter((col) =>
                hasValue(col, roundDetails.info),
              );
              if (visibleColumns.length === 0) return null;
              return (
                <Paper
                  elevation={2}
                  key={sectionTitle}
                  sx={{
                    mx: 1.5,
                    my: 3,
                    borderRadius: 2,
                    overflow: "hidden",
                    borderLeft: `4px solid ${accentColor}`,
                  }}
                >
                  {/* Section Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 2,
                      py: 1.25,
                      background: `${accentColor}14`,
                      borderBottom: `1px solid ${accentColor}30`,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={800}
                      sx={{
                        color: accentColor,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        fontSize: "14px",
                      }}
                    >
                      {sectionTitle.replace(/_/g, " ")}
                    </Typography>
                    {sectionTitle === "Match_Info" &&
                      (roundDetails.info?.gameBeBetInfo?.myTeamGoalScorers
                        ?.length > 0 ||
                        roundDetails.info?.gameBeBetInfo?.oppTeamGoalScorers
                          ?.length > 0) && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setGoalScorersModalOpen(true)}
                          sx={{
                            textTransform: "none",
                            fontSize: "11px",
                            py: 0.25,
                            borderColor: accentColor,
                            color: accentColor,
                          }}
                        >
                          View Goal Scorers
                        </Button>
                      )}
                  </Box>

                  {/* Fields Grid */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(260px, 1fr))",
                      gap: 1.5,
                      p: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    {visibleColumns.map((column) => {
                      const isFilterable = FILTERABLE_FIELD_IDS.has(column.id);
                      const description = FIELD_DESCRIPTIONS[column.id];
                      const rawValue = getNestedValue(
                        roundDetails.info,
                        column.id,
                      );
                      const canCopy =
                        isFilterable &&
                        rawValue != null &&
                        column.id !== "isFreeBet" &&
                        column.id !== "buyBonus";

                      return (
                        <Box
                          key={column.id}
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: isFilterable
                              ? `${accentColor}08`
                              : "grey.50",
                            border: "1px solid",
                            borderColor: isFilterable
                              ? `${accentColor}30`
                              : "grey.200",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                          }}
                        >
                          {/* Label row */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 800,
                                textTransform: "uppercase",
                                fontSize: "12px",
                                letterSpacing: 0.5,
                                color: isFilterable
                                  ? accentColor
                                  : "text.secondary",
                                lineHeight: 1,
                              }}
                            >
                              {column.label || column.id}
                            </Typography>
                            {description && (
                              <Tooltip
                                title={description}
                                arrow
                                placement="top"
                              >
                                <InfoOutlinedIcon
                                  sx={{
                                    fontSize: 11,
                                    color: accentColor,
                                    cursor: "help",
                                    opacity: 0.7,
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>

                          {/* Value row */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              justifyContent: "space-between",
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {renderCellValue(column, roundDetails.info)}
                            </Box>
                            {canCopy && (
                              <Tooltip title="Copy value" arrow placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() => copyValue(rawValue)}
                                  sx={{
                                    p: 0.25,
                                    color: "text.disabled",
                                    flexShrink: 0,
                                    "&:hover": { color: accentColor },
                                  }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              );
            })}

            {/* Match Events/Slots Section */}
            <MatchEventsTimeline
              slots={roundDetails.info?.gameBeBetInfo?.slots || []}
              allPlayers={roundDetails.info?.gameBeBetInfo?.allPlayers || []}
            />

            {/* JSON data sections — errorFromPlatform */}
            {[{ key: "errorFromPlatform", label: "Platform Error Data (JSON)" }]
              .filter(({ key }) => roundDetails.info?.[key] != null)
              .map(({ key, label }) => (
                <Paper
                  elevation={2}
                  key={key}
                  sx={{
                    mx: 1.5,
                    my: 3,
                    borderRadius: 2,
                    overflow: "hidden",
                    borderLeft: "4px solid #c62828",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 2,
                      py: 1.25,
                      background: "#c6282814",
                      borderBottom: "1px solid #c6282830",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleJsonSection(key)}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{
                        color: "#c62828",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        fontSize: "12px",
                      }}
                    >
                      {label}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyJson(roundDetails.info[key]);
                        }}
                        title="Copy JSON"
                        sx={{ color: "#c62828" }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      {jsonSections[key] ? (
                        <ExpandLessIcon sx={{ color: "#c62828" }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: "#c62828" }} />
                      )}
                    </Box>
                  </Box>
                  <Collapse in={jsonSections[key]}>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 2,
                        backgroundColor: "#1e1e1e",
                        color: "#d4d4d4",
                        fontSize: "12px",
                        fontFamily: "monospace",
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        maxHeight: 500,
                        overflowY: "auto",
                      }}
                    >
                      {JSON.stringify(roundDetails.info[key], null, 2)}
                    </Box>
                  </Collapse>
                </Paper>
              ))}
          </Box>
        )}
      </div>

      {/* Goal Scorers Modal */}
      <GoalScorersModal
        open={goalScorersModalOpen}
        onClose={() => setGoalScorersModalOpen(false)}
        myTeamGoalScorers={
          roundDetails.info?.gameBeBetInfo?.myTeamGoalScorers || []
        }
        oppTeamGoalScorers={
          roundDetails.info?.gameBeBetInfo?.oppTeamGoalScorers || []
        }
      />
      {reconcileOpen && roundDetails.info && (
        <ReconcileDialog
          open={reconcileOpen}
          onClose={() => setReconcileOpen(false)}
          betTxnId={roundDetails.betTxnId}
          status={roundDetails.info.status}
          betAmount={roundDetails.info.betAmount ?? 0}
          winAmount={roundDetails.info.winAmount ?? 0}
          currency={roundDetails.info.playerCurrencyCode}
          onSuccess={() => fetchData(roundDetails.betTxnId)}
        />
      )}
      {historyOpen && (
        <ReconciliationHistoryModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          betTxnId={roundDetails.betTxnId}
        />
      )}
    </ContentShell>
  );
};

export default RoundDetails;
